<template>
  <div>
    <div v-if="mode">
      <ul>
        <li>{{ nbAlleles }} alleles</li>
        <li>{{ nbGenotypes }} genotypes (total: {{ nbTotalGenotypes }})</li>
        <li>Reference length: {{ data.refseq_len }}</li>
        <li>
          Length (min:{{ data.len_stats.min }}, max:{{ data.len_stats.max }}, mean:
          {{ data.len_stats.mean }})
        </li>
        <li v-if="displayDistance">
          Distance (min:{{ distanceMin }}, max:{{ distanceMax }}, mean: {{ distanceMean }})
        </li>
      </ul>
    </div>
    <v-expansion-panels>
      <v-expansion-panel v-if="!mode">
        <v-expansion-panel-header>Generic stats</v-expansion-panel-header>
        <v-expansion-panel-content>
          <ul>
            <li>
              Number of alleles:
              {{ nbAlleles }}
            </li>
            <li>
              Number of genotypes:
              {{ nbGenotypes }}
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel v-if="!mode">
        <v-expansion-panel-header>Length stats</v-expansion-panel-header>
        <v-expansion-panel-content>
          <ul>
            <li>
              Max:
              {{ data.len_stats.max }}
            </li>
            <li>
              Min:
              {{ data.len_stats.min }}
            </li>
            <li>
              Mean:
              {{ data.len_stats.mean }}
            </li>
            <li>
              Standard deviation:
              {{ data.len_stats.standard_deviation }}
            </li>
            <li>
              Variance:
              {{ data.len_stats.variance }}
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel v-if="!mode">
        <v-expansion-panel-header>Distance stats</v-expansion-panel-header>
        <v-expansion-panel-content>
          <ul>
            <li>
              Max:
              {{ data.distance_stats.max }}
            </li>
            <li>
              Min:
              {{ data.distance_stats.min }}
            </li>
            <li>
              Mean:
              {{ data.distance_stats.mean }}
            </li>
            <li>
              Standard deviation:
              {{ data.distance_stats.standard_deviation }}
            </li>
            <li>
              Variance:
              {{ data.distance_stats.variance }}
            </li>
            <li>
              Sum:
              {{ data.distance_stats.sum }}
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Alleles</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-text-field
            v-model="searchAlleles"
            append-icon="search"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
          <v-data-table
            dense
            :headers="headersAlleles"
            :items="data.alleles"
            :items-per-page="5"
            class="elevation-1"
            :search="searchAlleles"
          >
            <template v-slot:[`item.span`]="{ item }">
              <span>{{ formatSpan(item.span) }}</span>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Genotypes</v-expansion-panel-header>
        <v-expansion-panel-content
          ><genotype-data-table :genotypes="data.genotypes" :show-select="false">
          </genotype-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Missing genotypes</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-text-field
            v-model="searchMissingGenotypes"
            append-icon="search"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
          <v-data-table
            dense
            :headers="headersMissingGenotypes"
            :items="missingForTable"
            :items-per-page="5"
            class="elevation-1"
            :search="searchMissingGenotypes"
          ></v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import _ from 'lodash';
import GenotypeDataTable from '@/components/genotype/GenotypeDataTable.vue';

export default {
  components: { GenotypeDataTable },
  props: {
    data: {
      type: Object,
      default: () => {
        return {};
      },
    },
    mode: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      searchAlleles: '',
      searchGenotypes: '',
      searchMissingGenotypes: '',
      headersAlleles: [
        {
          text: 'allele',
          align: 'left',
          sortable: true,
          value: 'name',
        },
        {
          text: 'refspan%',
          align: 'left',
          sortable: true,
          value: 'span',
        },
        {
          text: 'gaps',
          align: 'left',
          sortable: true,
          value: 'gaps',
        },
        {
          text: 'Length',
          align: 'left',
          sortable: true,
          value: 'seqlen',
        },
      ],
      headersMissingGenotypes: [
        {
          text: 'genotype',
          align: 'left',
          sortable: true,
          value: 'name',
        },
      ],
    };
  },
  computed: {
    missingForTable() {
      return this.data.missings.map(m => {
        return { name: m };
      });
    },
    hasData() {
      return !_.isEmpty(this.data);
    },
    nbAlleles() {
      return this.data.alleles ? Object.keys(this.data.alleles).length : 0;
    },
    nbGenotypes() {
      return this.data.genotypes ? Object.keys(this.data.genotypes).length : 0;
    },
    distanceMin() {
      return this.data.distance_stats
        ? Math.round(parseFloat(this.data.distance_stats.min) * 100) / 100
        : 'NA';
    },
    distanceMax() {
      return this.data.distance_stats
        ? Math.round(parseFloat(this.data.distance_stats.max) * 100) / 100
        : 'NA';
    },
    distanceMean() {
      return this.data.distance_stats
        ? Math.round(parseFloat(this.data.distance_stats.mean) * 100) / 100
        : 'NA';
    },
    nbTotalGenotypes() {
      return this.data.genotypes.length + this.data.missings.length;
    },
    displayDistance() {
      return !isNaN(this.distanceMin) || !isNaN(this.distanceMax) || !isNaN(this.distanceMean);
    },
  },
  methods: {
    formatSpan(s) {
      return Math.round(s * 10000) / 100;
    },
  },
};
</script>

<style scoped>
/** marche pas */
.v-text-field__slot input {
  text-align: right;
}
</style>
